import { useState, useEffect } from 'react';
import { collection, getDocs, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Custom hook to fetch users from Firebase Firestore
 * 
 * @param {Object} options - Configuration options
 * @param {Boolean} options.realtime - Enable real-time updates (default: false)
 * @param {String} options.type - Filter by user type ('artist' | 'viewer' | null)
 * @param {Boolean} options.activeOnly - Only fetch active users (default: false)
 * 
 * @returns {Object} { users, loading, error, refetch }
 */

export const useUsers = (options = {}) => {
    const {
        realtime = false,
        type = null,
        activeOnly = false,
    } = options;

    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch users function
    const fetchUsers = async () => {
        try {
            setLoading(true);
            setError(null);

            // Build query
            let q = collection(db, 'Users');
            const constraints = [];

            // Filter by type
            if (type) {
                constraints.push(where('type', '==', type));
            }

            // Filter active only
            if (activeOnly) {
                constraints.push(where('is_deleted', '==', false));
            }

            // Add ordering
            constraints.push(orderBy('created_at', 'desc'));

            // Create query with constraints
            if (constraints.length > 0) {
                q = query(collection(db, 'Users'), ...constraints);
            }

            // Get documents
            const querySnapshot = await getDocs(q);
            
            const usersData = [];
            querySnapshot.forEach((doc) => {
                usersData.push({
                    id: doc.id,
                    ...doc.data()
                });
            });

            setUsers(usersData);
            setLoading(false);

            return usersData;
        } catch (err) {
            console.error('Error fetching users:', err);
            setError(err.message);
            setLoading(false);
            return [];
        }
    };

    // Set up real-time listener if enabled
    useEffect(() => {
        if (realtime) {
            // Build query
            let q = collection(db, 'Users');
            const constraints = [];

            if (type) {
                constraints.push(where('type', '==', type));
            }

            if (activeOnly) {
                constraints.push(where('is_deleted', '==', false));
            }

            constraints.push(orderBy('created_at', 'desc'));

            if (constraints.length > 0) {
                q = query(collection(db, 'Users'), ...constraints);
            }

            // Set up real-time listener
            const unsubscribe = onSnapshot(
                q,
                (querySnapshot) => {
                    const usersData = [];
                    querySnapshot.forEach((doc) => {
                        usersData.push({
                            id: doc.id,
                            ...doc.data()
                        });
                    });
                    setUsers(usersData);
                    setLoading(false);
                },
                (err) => {
                    console.error('Error in real-time listener:', err);
                    setError(err.message);
                    setLoading(false);
                }
            );

            // Cleanup listener
            return () => unsubscribe();
        } else {
            // Fetch once
            fetchUsers();
        }
    }, [realtime, type, activeOnly]);

    // Refetch function
    const refetch = () => {
        return fetchUsers();
    };

    return {
        users,
        loading,
        error,
        refetch,
    };
};

/**
 * Get user statistics
 */
export const getUserStats = (users) => {
    const totalUsers = users.filter(user =>
        user.type !== 'admin' && user.type !== 'superAdmin' && user.type && !user.is_deleted
    ).length;
    const viewers = users.filter(user => user.type === 'viewer').length;
    const artists = users.filter(user => user.type === 'artist').length;
    const activeUsers = users.filter(user =>
        user.type !== 'admin' && user.type !== 'superAdmin' && !user.is_deleted
    ).length;
    const deletedUsers = users.filter(user => user.is_deleted).length;
    

    return {
        totalUsers,
        viewers,
        artists,
        activeUsers,
        deletedUsers,
    };
};